import { FC, useRef, useState } from "react";
import { FormikHelpers, FormikProps } from "formik";
import {
  Button,
  Form,
  FormButtons,
  FormField,
  MessageField,
  Modal,
  useAutocomplete,
  useFileParser,
  FormField as UiKitFormField,
} from "@epcnetwork/core-ui-kit";

import { filterDatabasesPipelines } from "utils";
import { suppressionCheckboxLabel } from "constants/pipelines.constants";
import { FormField as LocalFormField } from "components";
import { getDatabasesNames } from "api";
import { ReverseFileItem, ReverseValues } from "../reverse.types";
import { validationSchema } from "../reverse.constants";
import { HashTypes } from "./hash-types";

import styles from "../reverse.module.css";

type ReverseModalFormProps = {
  file: ReverseFileItem;
  setClose: VoidFunction;
  onSubmitClick: (fileValues: ReverseFileItem) => void;
};

export const ReverseFormModal: FC<ReverseModalFormProps> = ({ file, setClose, onSubmitClick }) => {
  const { loading, parsedFile, error } = useFileParser(file.originalFile);

  const [initialValues] = useState<ReverseValues>({
    fileValues: {
      headers: file.data.headers,
      suppress: file.data.suppress,
      databases: file.data.databases,
      hashType: file.data.hashType,
    },
  });

  const formRef = useRef<FormikProps<ReverseValues> | null>(null);

  const databasesAutocomplete = useAutocomplete(getDatabasesNames, "name", "name");
  databasesAutocomplete.fetchOptions = filterDatabasesPipelines(databasesAutocomplete.fetchOptions);

  const onSubmit = (values: ReverseValues, { setSubmitting }: FormikHelpers<ReverseValues>) => {
    const isDialmap = values.fileValues.databases.includes("dialmap");
    const fileValues = {
      ...values.fileValues,
      suppress: isDialmap ? false : values.fileValues.suppress || false,
    };

    onSubmitClick({
      ...file,
      isTuned: true,
      data: {
        ...file.data,
        ...fileValues,
      },
    });
    setSubmitting(false);
  };

  return (
    <Modal isOpen={Boolean(file)} setClose={setClose} contentClassName={styles.modal}>
      <h2>Set values {file.originalFile.name}</h2>
      <MessageField message={error} />
      <Form
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
        loading={loading}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        {({ values, setFieldValue }) => {
          const isDialmap = values.fileValues.databases.includes("dialmap");

          const getOptions = () => {
            if (values.fileValues.databases.length === 0) return databasesAutocomplete.fetchOptions;

            return databasesAutocomplete.fetchOptions.map((option) =>
              option.value === "dialmap"
                ? { ...option, locked: !isDialmap }
                : { ...option, locked: isDialmap },
            );
          };

          const onSelectAll = async () => {
            /* select all databases except dialmap */
            const allDatabases = databasesAutocomplete.fetchOptions
              .map((option) => option.value)
              .filter((value) => value !== "dialmap");

            await setFieldValue("fileValues.databases", allDatabases);
          };

          const autocomplete = { ...databasesAutocomplete, fetchOptions: getOptions() };

          return (
            <>
              <FormField
                type="select"
                name="fileValues.databases"
                label="Databases"
                placeholder="Databases"
                asyncOptions={autocomplete}
                isMulti
                canSelectAll
                onSelectAll={onSelectAll}
              />
              <HashTypes />
              <LocalFormField
                type="file-table"
                name="fileValues"
                label={isDialmap ? "Select Phone column" : "Select Email column"}
                parsedFile={parsedFile}
                loading={loading}
                selector="radio"
                autocompleteColumns={false}
              />

              <div className={styles.actions}>
                {isDialmap ? (
                  <div />
                ) : (
                  <UiKitFormField
                    type="checkbox"
                    name="fileValues.suppress"
                    label={suppressionCheckboxLabel}
                    disableError
                  />
                )}

                <FormButtons>
                  <Button appearance="secondary" onClick={setClose}>
                    Cancel
                  </Button>
                  <Button type="submit">Set values</Button>
                </FormButtons>
              </div>
            </>
          );
        }}
      </Form>
    </Modal>
  );
};
